import { useState, useEffect, useCallback } from 'react';
import { Save, RotateCcw, Copy, RefreshCw, Eye, EyeOff, AlertCircle, Loader2, Plus, X } from 'lucide-react';
import { Button, Heading, Skeleton, TableScroll } from '@aaimplatform/ui';
import { usePlatformConfig } from '../../hooks/usePlatformConfig';
import { apiClient } from '../../api/client';
import type { AuthenticationService } from '@aaimplatform/institutional-types';

type ApiKey = AuthenticationService.components['schemas']['ApiKey'];
const API_DEFAULTS = {
  rateLimit: 1000,
  rateLimitWindow: 60,
  webhookEnabled: true,
  webhookUrl: 'https://api.example.com/webhooks/aaim',
  webhookSecret: 'whsec_xxxxxxxxxxxxxxxxxxxxx',
  apiKeys: [] as ApiKey[],
};

type ApiSettings = typeof API_DEFAULTS;

/**
 * API base for the admin portal config page.
 *
 * Sprint C13 WP-2 (2026-04-30): the previous value `/api/admin/platform`
 * never resolved -- platform-admin-api mounts adminPortalRoutes at
 * `/api/admin` (server.ts:386) so the router saw `/platform/settings/...`
 * which is unmatched -> 404 (and 401 from the JWT gate before the router
 * because raw fetch() omitted the Bearer header). The four call sites
 * below were also migrated from raw fetch() to the legacy `apiClient`
 * (Bearer-injecting wrapper) which fixes the 401.
 *
 * `apiClient` is constructed with baseUrl='/api' (see ../../../api/client),
 * so this prefix omits the `/api` segment -- the resulting URLs are
 * `/api/admin/settings/...` and `/api/admin/config/api`.
 *
 * DEPRECATED (follow-on C13->C14-2): the four endpoints used here
 * (`/settings/webhook-secret/regenerate`, `/settings/api-keys`,
 * `/settings/api-keys/{keyId}`, `/config/{domain}`) are not yet in
 * platform-admin.openapi.yaml. Once added, this page should switch to
 * the openapi-fetch typed `platformAdminClient` per
 * `.claude/rules/typescript-compliance.md`.
 */
const API_BASE = '/admin';

interface RegenerateSecretResponse {
  success: boolean;
  webhookSecret: string;
}

interface CreateApiKeyResponse {
  success: boolean;
  key: {
    id: string;
    name: string;
    plainTextKey: string;
    expiresAt: string | null;
    createdAt: string;
  };
}

interface ConfigApiDomainResponse {
  webhookSecret?: string;
  apiKeys?: ApiKey[];
}

/**
 * API Configuration - Platform Admin
 *
 * Rate limits, webhooks, and API keys
 */
export function ApiConfigPage() {
  const { config, isLoading, error, save, isSaving, saveError } =
    usePlatformConfig<ApiSettings>('api');
  const [hasChanges, setHasChanges] = useState(false);
  const [showSecret, setShowSecret] = useState(false);
  const [settings, setSettings] = useState<ApiSettings>(API_DEFAULTS);
  const [isRegenerating, setIsRegenerating] = useState(false);
  const [showConfirmRegenerate, setShowConfirmRegenerate] = useState(false);
  const [showCreateKey, setShowCreateKey] = useState(false);
  const [newKeyName, setNewKeyName] = useState('');
  const [newKeyExpiry, setNewKeyExpiry] = useState('');
  const [isCreatingKey, setIsCreatingKey] = useState(false);
  const [createdKey, setCreatedKey] = useState<string | null>(null);

  const handleRegenerateSecret = useCallback(async () => {
    setIsRegenerating(true);
    try {
      const data = await apiClient.post<RegenerateSecretResponse>(
        `${API_BASE}/settings/webhook-secret/regenerate`,
      );
      setSettings(prev => ({ ...prev, webhookSecret: data.webhookSecret }));
      setShowConfirmRegenerate(false);
    } catch {
      // Error is shown via saveError state
    } finally {
      setIsRegenerating(false);
    }
  }, []);

  const handleCreateKey = useCallback(async () => {
    if (!newKeyName.trim()) return;
    setIsCreatingKey(true);
    try {
      const data = await apiClient.post<CreateApiKeyResponse>(
        `${API_BASE}/settings/api-keys`,
        {
          name: newKeyName.trim(),
          expiresInDays: newKeyExpiry ? parseInt(newKeyExpiry, 10) : undefined,
        },
      );
      setCreatedKey(data.key.plainTextKey);
      // Add key to local state (without plaintext)
      setSettings(prev => ({
        ...prev,
        apiKeys: [...prev.apiKeys, {
          id: data.key.id,
          name: data.key.name,
          createdAt: data.key.createdAt,
          expiresAt: data.key.expiresAt,
          lastUsedAt: null,
        } as ApiKey],
      }));
    } catch {
      // Error handling
    } finally {
      setIsCreatingKey(false);
    }
  }, [newKeyName, newKeyExpiry]);

  const handleRevokeKey = useCallback(async (keyId: string) => {
    try {
      await apiClient.delete<unknown>(`${API_BASE}/settings/api-keys/${keyId}`);
      setSettings(prev => ({
        ...prev,
        apiKeys: prev.apiKeys.map(k =>
          k.id === keyId ? { ...k, expiresAt: 'REVOKED' } as ApiKey : k
        ),
      }));
    } catch {
      // Error handling
    }
  }, []);

  // Load API-domain config (webhook secret, API keys) from InstitutionConfig store.
  // Rate limit settings come from usePlatformConfig; webhook/key data comes from /config/api.
  useEffect(() => {
    async function loadApiConfig() {
      try {
        const data = await apiClient.get<ConfigApiDomainResponse>(
          `${API_BASE}/config/api`,
        );
        setSettings(prev => ({
          ...prev,
          webhookSecret: data.webhookSecret ?? prev.webhookSecret,
          apiKeys: Array.isArray(data.apiKeys) ? data.apiKeys : prev.apiKeys,
        }));
      } catch {
        // Config not yet created for this tenant -- use defaults
      }
    }
    loadApiConfig();
  }, []);

  useEffect(() => {
    if (config) {
      // Merge platform settings (rate limits, webhook URL/enabled) without
      // overwriting webhook secret and API keys loaded from InstitutionConfig
      setSettings(prev => ({
        ...prev,
        rateLimit: config.rateLimit ?? prev.rateLimit,
        rateLimitWindow: config.rateLimitWindow ?? prev.rateLimitWindow,
        webhookEnabled: config.webhookEnabled ?? prev.webhookEnabled,
        webhookUrl: config.webhookUrl ?? prev.webhookUrl,
      }));
      setHasChanges(false);
    }
  }, [config]);

  const handleChange = (field: string, value: string | number | boolean) => {
    setSettings(prev => ({ ...prev, [field]: value }));
    setHasChanges(true);
  };

  const handleSave = () => {
    save(settings);
    setHasChanges(false);
  };

  const handleReset = () => {
    if (config) setSettings(config);
    setHasChanges(false);
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  if (isLoading) {
    return (
      <div>
        <div className="flex items-center justify-end gap-3 mb-6">
          <Skeleton className="h-10 w-24" />
          <Skeleton className="h-10 w-36" />
        </div>
        <Skeleton className="h-40 rounded-sm mb-6" />
        <Skeleton className="h-48 rounded-sm mb-6" />
        <Skeleton className="h-48 rounded-sm" />
      </div>
    );
  }

  return (
    <div>
      {/* Action Buttons */}
      <div className="flex flex-wrap items-center justify-end gap-3 mb-6">
        <Button
          variant="secondary"
          onClick={handleReset}
          disabled={!hasChanges}
        >
          <RotateCcw className="w-4 h-4" />
          Reset
        </Button>
        <Button
          variant="primary"
          onClick={handleSave}
          disabled={!hasChanges || isSaving}
        >
          {isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          {isSaving ? 'Saving...' : 'Save Changes'}
        </Button>
      </div>

      {/* Error State */}
      {(error || saveError) && (
        <div className="bg-error/5 border border-error/20 rounded-sm p-4 mb-6 flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-error flex-shrink-0" />
          <div className="text-sm font-sans text-error">
            {error ? 'Failed to load configuration' : 'Failed to save changes'}
          </div>
        </div>
      )}

      {/* Rate Limiting */}
      <div className="bg-surface rounded-sm shadow-sm p-6 mb-6">
        <Heading level={3} className="mb-6">
          Rate Limiting
        </Heading>

        <div className="space-y-6">
          <div className="flex items-center justify-between py-3 border-b border-recessed">
            <div>
              <div className="text-sm font-sans font-medium text-primary leading-normal">
                Requests per window
              </div>
              <div className="text-xs font-sans text-tertiary leading-normal mt-1">
                Maximum API requests allowed per time window
              </div>
            </div>
            <input
              type="number"
              value={settings.rateLimit}
              onChange={e => handleChange('rateLimit', parseInt(e.target.value))}
              className="w-28 px-3 py-2 border border-recessed rounded-sm text-sm font-mono text-center focus:outline-none focus:border-accent"
            />
          </div>

          <div className="flex items-center justify-between py-3">
            <div>
              <div className="text-sm font-sans font-medium text-primary leading-normal">
                Time window (seconds)
              </div>
              <div className="text-xs font-sans text-tertiary leading-normal mt-1">
                Duration of rate limit window
              </div>
            </div>
            <input
              type="number"
              value={settings.rateLimitWindow}
              onChange={e => handleChange('rateLimitWindow', parseInt(e.target.value))}
              className="w-28 px-3 py-2 border border-recessed rounded-sm text-sm font-mono text-center focus:outline-none focus:border-accent"
            />
          </div>
        </div>
      </div>

      {/* Webhooks */}
      <div className="bg-surface rounded-sm shadow-sm p-6 mb-6">
        <div className="flex items-center justify-between mb-6">
          <Heading level={3}>
            Webhooks
          </Heading>
          <button
            onClick={() => handleChange('webhookEnabled', !settings.webhookEnabled)}
            className={`w-12 h-6 rounded-full transition-colors ${
              settings.webhookEnabled ? 'bg-accent' : 'bg-recessed'
            }`}
          >
            <div
              className={`w-5 h-5 bg-surface rounded-full shadow transition-transform ${
                settings.webhookEnabled ? 'translate-x-6' : 'translate-x-0.5'
              }`}
            />
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <label className="text-sm font-sans font-medium text-primary leading-normal block mb-2">
              Webhook URL
            </label>
            <input
              type="url"
              value={settings.webhookUrl}
              onChange={e => handleChange('webhookUrl', e.target.value)}
              disabled={!settings.webhookEnabled}
              className={`w-full px-3 py-2 border border-recessed rounded-sm text-sm font-mono focus:outline-none focus:border-accent ${
                !settings.webhookEnabled ? 'bg-elevated text-tertiary' : ''
              }`}
            />
          </div>

          <div>
            <label className="text-sm font-sans font-medium text-primary leading-normal block mb-2">
              Webhook Secret
            </label>
            <div className="flex gap-2">
              <div className="flex-1 relative">
                <input
                  type={showSecret ? 'text' : 'password'}
                  value={settings.webhookSecret}
                  readOnly
                  className="w-full px-3 py-2 border border-recessed rounded-sm text-sm font-mono bg-elevated"
                />
                <button
                  onClick={() => setShowSecret(!showSecret)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-1 hover:bg-recessed rounded-sm"
                >
                  {showSecret ? <EyeOff className="w-4 h-4 text-tertiary" /> : <Eye className="w-4 h-4 text-tertiary" />}
                </button>
              </div>
              <Button variant="ghost" size="sm" onClick={() => copyToClipboard(settings.webhookSecret)} aria-label="Copy secret">
                <Copy className="w-4 h-4" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                aria-label="Regenerate secret"
                onClick={() => setShowConfirmRegenerate(true)}
                disabled={isRegenerating}
              >
                {isRegenerating ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* API Keys */}
      <div className="bg-surface rounded-sm shadow-sm p-6">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-6">
          <Heading level={3}>
            API Keys
          </Heading>
          <Button variant="primary" onClick={() => { setShowCreateKey(true); setNewKeyName(''); setNewKeyExpiry(''); setCreatedKey(null); }}>
            <Plus className="w-4 h-4" />
            Create New Key
          </Button>
        </div>

        {settings.apiKeys.length === 0 ? (
          <div className="text-center py-8">
            <div className="text-sm font-sans text-tertiary">No API keys configured</div>
          </div>
        ) : (
          <TableScroll label="API keys">
            <table className="w-full">
              <thead>
                <tr className="border-b border-recessed">
                  <th className="text-left py-3 text-label-xs font-sans font-semibold uppercase text-tertiary leading-normal">
                    Name
                  </th>
                  <th className="text-left py-3 text-label-xs font-sans font-semibold uppercase text-tertiary leading-normal">
                    Key ID
                  </th>
                  <th className="text-left py-3 text-label-xs font-sans font-semibold uppercase text-tertiary leading-normal">
                    Created
                  </th>
                  <th className="text-left py-3 text-label-xs font-sans font-semibold uppercase text-tertiary leading-normal">
                    Last Used
                  </th>
                  <th className="text-left py-3 text-label-xs font-sans font-semibold uppercase text-tertiary leading-normal">
                    Status
                  </th>
                  <th className="text-left py-3 text-label-xs font-sans font-semibold uppercase text-tertiary leading-normal">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {settings.apiKeys.map(key => (
                  <tr key={key.id} className="border-b border-recessed hover:bg-elevated transition-colors">
                    <td className="py-3 text-sm font-sans font-medium text-primary leading-normal">
                      {key.name}
                    </td>
                    <td className="py-3 text-sm font-mono text-secondary leading-normal">
                      {key.id}
                    </td>
                    <td className="py-3 text-sm font-sans text-secondary leading-normal">
                      {key.createdAt}
                    </td>
                    <td className="py-3 text-sm font-sans text-secondary leading-normal">
                      {key.lastUsedAt ?? '--'}
                    </td>
                    <td className="py-3">
                      {key.expiresAt === 'REVOKED' ? (
                        <span className="px-2 py-0.5 text-xs font-sans font-semibold bg-error/10 text-error rounded leading-normal">
                          Revoked
                        </span>
                      ) : key.expiresAt && new Date(key.expiresAt) < new Date() ? (
                        <span className="px-2 py-0.5 text-xs font-sans font-semibold bg-warning/10 text-warning rounded leading-normal">
                          Expired
                        </span>
                      ) : (
                        <span className="px-2 py-0.5 text-xs font-sans font-semibold bg-growth/10 text-growth rounded leading-normal">
                          Active
                        </span>
                      )}
                    </td>
                    <td className="py-3">
                      {key.expiresAt !== 'REVOKED' && (
                        <Button variant="ghost" size="sm" onClick={() => handleRevokeKey(key.id)}>
                          Revoke
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </TableScroll>
        )}
      </div>

      {/* Regenerate Secret Confirmation Dialog */}
      {showConfirmRegenerate && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-surface rounded-sm shadow-lg p-6 max-w-md mx-4">
            <div className="flex items-center gap-3 mb-4">
              <AlertCircle className="w-5 h-5 text-error flex-shrink-0" />
              <Heading level={4}>Regenerate Webhook Secret</Heading>
            </div>
            <p className="text-sm font-sans text-secondary mb-6">
              This will invalidate the current webhook secret. Your endpoint will stop receiving
              webhook deliveries until you update it with the new secret. This action cannot be undone.
            </p>
            <div className="flex justify-end gap-3">
              <Button variant="secondary" onClick={() => setShowConfirmRegenerate(false)}>
                Cancel
              </Button>
              <Button variant="primary" onClick={handleRegenerateSecret} disabled={isRegenerating}>
                {isRegenerating ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                {isRegenerating ? 'Regenerating...' : 'Regenerate'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Create API Key Dialog */}
      {showCreateKey && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-surface rounded-sm shadow-lg p-6 max-w-md mx-4 w-full">
            <div className="flex items-center justify-between mb-4">
              <Heading level={4}>Create API Key</Heading>
              <button onClick={() => { setShowCreateKey(false); setCreatedKey(null); }} className="p-1 hover:bg-recessed rounded-sm">
                <X className="w-4 h-4 text-tertiary" />
              </button>
            </div>

            {createdKey ? (
              <div>
                <p className="text-sm font-sans text-secondary mb-3">
                  Your API key has been created. Copy it now -- it will not be shown again.
                </p>
                <div className="flex gap-2 mb-6">
                  <input
                    type="text"
                    value={createdKey}
                    readOnly
                    className="flex-1 px-3 py-2 border border-recessed rounded-sm text-sm font-mono bg-elevated"
                  />
                  <Button variant="ghost" size="sm" onClick={() => navigator.clipboard.writeText(createdKey)}>
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
                <div className="flex justify-end">
                  <Button variant="primary" onClick={() => { setShowCreateKey(false); setCreatedKey(null); }}>
                    Done
                  </Button>
                </div>
              </div>
            ) : (
              <div>
                <div className="space-y-4 mb-6">
                  <div>
                    <label className="text-sm font-sans font-medium text-primary block mb-2">
                      Key Name
                    </label>
                    <input
                      type="text"
                      value={newKeyName}
                      onChange={e => setNewKeyName(e.target.value)}
                      placeholder="Production API Key"
                      className="w-full px-3 py-2 border border-recessed rounded-sm text-sm font-sans focus:outline-none focus:border-accent"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-sans font-medium text-primary block mb-2">
                      Expires In (days, optional)
                    </label>
                    <input
                      type="number"
                      value={newKeyExpiry}
                      onChange={e => setNewKeyExpiry(e.target.value)}
                      placeholder="365"
                      min="1"
                      className="w-full px-3 py-2 border border-recessed rounded-sm text-sm font-mono focus:outline-none focus:border-accent"
                    />
                  </div>
                </div>
                <div className="flex justify-end gap-3">
                  <Button variant="secondary" onClick={() => setShowCreateKey(false)}>
                    Cancel
                  </Button>
                  <Button variant="primary" onClick={handleCreateKey} disabled={isCreatingKey || !newKeyName.trim()}>
                    {isCreatingKey ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                    {isCreatingKey ? 'Creating...' : 'Create Key'}
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
